import axiosInstance from './apiService';
import { toast } from 'react-toastify';
import { BrandBanner } from '@/interfaces/Brand';

export const getBrands = async (
  page: number,
  limit: number,
  searchTerm?: string,
) => {
  try {
    const response = await axiosInstance.get(
      `/admin/get-brands?page=${page}&limit=${limit}${searchTerm ? `&keyword=${searchTerm}` : ''}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch brand');
  }
};
export const getBrandMemberList = async (
  page: number,
  limit: number,
  searchTerm?: string,
  brandId?: string,
) => {
  try {
    const response = await axiosInstance.get(
      `/brand/${brandId}/get-brand-user-list?${searchTerm ? `search=${searchTerm}&` : ''}page=${page}&limit=${limit}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch brand');
  }
};

export const createBrand = (formData: any) => {
  return axiosInstance.post(`/admin/create-brand`, formData);
};

export const getBrandById = async (brandId: string) => {
  try {
    const response = await axiosInstance.get(
      `/admin/get-brand-by-id/${brandId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch brand by ID');
  }
};

export const addDeposit = (brandId: string, formData: any) => {
  return axiosInstance.post(`/deposit/${brandId}/add-deposit`, formData);
};

export const addLicense = (brandId: string, formData: any) => {
  return axiosInstance.post(`/admin/brand/${brandId}/add-license`, formData);
};

export const getBrandLicenses = async (
  brandId: string,
  page = 1,
  limit = 10,
) => {
  try {
    const response = await axiosInstance.get(
      `/admin/brand/${brandId}/get-license?page=${page}&limit=${limit}`,
    );
    return response.data?.response ?? response.data;
  } catch (error) {
    throw new Error('Failed to fetch brand licenses');
  }
};

export const getBrandDeposit = async (
  brandId: string,
  page: number,
  limit: number,
) => {
  try {
    const response = await axiosInstance.get(
      `deposit/brand/${brandId}/get-deposit?page=${page}&limit=${limit}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch brand deposit');
  }
};

export const updateBrandLogo = async (brandId: string, formData: FormData) => {
  try {
    const response = await axiosInstance.post(
      `/brand/upload/${brandId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when updating brand', error);
    return null;
  }
};

export const uploadBrandImage = async (
  file: File,
): Promise<{ key: string; url: string }> => {
  const response = await axiosInstance.postForm(`/admin/images/upload`, {
    file,
  });
  return response.data;
};

export const setBrandBanners = async (
  brandId: string,
  payload: { keys: string[]; logo?: string },
): Promise<{ banners: BrandBanner[] }> => {
  const response = await axiosInstance.put(
    `/admin/brands/${brandId}/banners`,
    payload,
  );
  return response.data;
};

export const setBrandTagline = async (
  brandId: string,
  tagline: string,
): Promise<{ tagline: string | null }> => {
  const response = await axiosInstance.put(`/admin/brands/${brandId}/tagline`, {
    tagline,
  });
  return response.data;
};

export const setBrandFeaturedBanners = async (
  brandId: string,
  payload: { keys: string[]; logo?: string; isFeatured?: boolean },
): Promise<{ banners: BrandBanner[] }> => {
  const response = await axiosInstance.put(
    `/admin/brands/${brandId}/featured-banners`,
    payload,
  );
  return response.data;
};

export const impersonateBrand = async (
  brandId: string,
): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
} | null> => {
  try {
    const response = await axiosInstance.post(`/admin/impersonate/${brandId}`);
    return response.data;
  } catch (error) {
    console.error('Error impersonating brand:', error);
    toast.error('Failed to impersonate brand');
    return null;
  }
};
